import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * PWAInstallBanner — small install prompt shown only on mobile screens.
 *
 * Behaviour:
 *  - Listens for the `beforeinstallprompt` event (Chrome / Edge / Samsung /
 *    modern Android browsers) and stores the deferred prompt.
 *  - Also detects iOS Safari (which has no programmatic prompt) and shows
 *    a short "Adiciona ao ecrã principal" tooltip.
 *  - Hides itself when:
 *      * the app is already running in standalone mode (installed).
 *      * the user has dismissed the banner (persisted in localStorage).
 *      * the viewport is wider than 900px (desktop — no benefit).
 */
const DISMISSED_KEY = "supert_pwa_dismissed_v1";

function isStandalone() {
  if (typeof window === "undefined") return false;
  const mm = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  return mm || window.navigator.standalone === true;
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIos, setIsIos] = useState(false);
  // Hide in landscape on mobile: a bottom install banner is intrusive during
  // landscape gameplay, and on iOS-landscape the MobileFullscreen component
  // already shows the "Adicionar ao Ecrã Principal" hint — this prevents two
  // near-identical prompts appearing at once.
  const [landscape, setLandscape] = useState(
    () => (window.innerWidth || 0) > (window.innerHeight || 0)
  );

  useEffect(() => {
    const update = () => setLandscape((window.innerWidth || 0) > (window.innerHeight || 0));
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    if (window.visualViewport) window.visualViewport.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      if (window.visualViewport) window.visualViewport.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    // Never show on desktop, standalone, or if previously dismissed.
    if (isStandalone()) return undefined;
    if (window.innerWidth > 900) return undefined;
    if (localStorage.getItem(DISMISSED_KEY) === "1") return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari doesn't fire beforeinstallprompt — detect and show helper text.
    if (isIOS()) {
      setIsIos(true);
      // Only auto-show after 1.5s so it doesn't compete with the splash.
      const t = setTimeout(() => setVisible(true), 1500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch { /* user cancelled — ignore */ }
    setDeferredPrompt(null);
    setVisible(false);
    localStorage.setItem(DISMISSED_KEY, "1");
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "1");
    setVisible(false);
  };

  if (!visible || landscape) return null;

  return (
    <div
      className="fixed left-3 right-3 bottom-3 z-[9999] rounded-2xl px-4 py-3 flex items-center gap-3 shadow-2xl"
      style={{
        background: "linear-gradient(180deg, #0F1A55 0%, #0A0E2A 100%)",
        border: "1px solid rgba(61,165,255,0.55)",
        boxShadow: "0 0 24px rgba(61,165,255,0.35), 0 12px 40px -8px rgba(0,0,0,0.6)",
      }}
      data-testid="pwa-install-banner"
    >
      <div
        className="grid place-items-center flex-shrink-0 rounded-xl"
        style={{ width: 44, height: 44, background: "#FFD93D" }}
        aria-hidden
      >
        <Download style={{ width: 22, height: 22, color: "#0A0E2A" }} strokeWidth={2.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-[Fredoka] font-black text-white text-sm leading-tight">
          Instala o SUPERTMATIK
        </div>
        <div className="text-[11px] text-slate-300/90 leading-tight mt-0.5">
          {isIos
            ? "Toca em Partilhar → Adicionar ao Ecrã Principal"
            : "Fica com atalho no telemóvel — 1 toque"}
        </div>
      </div>
      {!isIos && deferredPrompt && (
        <button
          onClick={handleInstall}
          className="font-[Fredoka] font-black uppercase text-xs px-4 py-2 rounded-full flex-shrink-0 hover:scale-105 transition"
          style={{
            background: "#E5FF00",
            color: "#0A0E2A",
            boxShadow: "0 0 12px rgba(229,255,0,0.5), inset 0 -2px 0 rgba(0,0,0,0.18)",
          }}
          data-testid="pwa-install-btn"
        >
          Instalar
        </button>
      )}
      <button
        onClick={handleDismiss}
        aria-label="Dispensar"
        className="flex-shrink-0 rounded-full grid place-items-center hover:bg-white/10 transition"
        style={{ width: 32, height: 32 }}
        data-testid="pwa-dismiss-btn"
      >
        <X style={{ width: 18, height: 18, color: "rgba(255,255,255,0.7)" }} />
      </button>
    </div>
  );
}
